"use client";

import React, { useEffect, useState } from "react";
import {
  DollarSign,
  Briefcase,
  TrendingUp,
  ChevronRight,
  Loader2,
  Mail,
} from "lucide-react";
import toast from "react-hot-toast";
import WorkerDetails from "./WorkerDetails";
import { REST_API_URL } from "../../api/config";

const API_URL = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function YourBusinessWorkers() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorker, setSelectedWorker] = useState(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        setLoading(true);
        
        const headers = buildHeaders(token);
        
        const res = await fetch(`${API_URL}/auth/workers-list/`, {
          headers,
        });

        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.detail || `HTTP ${res.status}`);
        }

        const data = await res.json();
        setWorkers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("❌ Failed to fetch workers:", err);
        toast.error("Failed to load workers list");
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchWorkers();
  }, [token]);

  if (selectedWorker) {
    return (
      <WorkerDetails
        worker={selectedWorker}
        onBack={() => setSelectedWorker(null)}
      />
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 w-full flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[20px] font-bold text-[#302B2B]">Workers list</h3>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-10">
          <Loader2 className="w-6 h-6 text-[#5563F6] animate-spin" />
        </div>
      )}

      {!loading && workers.length === 0 && (
        <p className="text-center text-[#9A99AB] py-6">No workers found</p>
      )}

      {!loading && workers.length > 0 && (
        <div className="flex flex-col gap-4 max-h-[650px] overflow-y-auto pr-2">
          {workers.map((w, i) => {
            const isUnregistered = !w.full_name || w.full_name.trim() === "";
            return (
              <div
                key={i}
                onClick={() => !isUnregistered && setSelectedWorker(w)}
                className={`flex flex-col border border-[#EDEEF3] rounded-2xl px-4 py-3 transition
                  ${isUnregistered ? "bg-gray-50 cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-[#F9FAFB]"}
                `}
              >
                <div className="flex items-center gap-3 mb-3">
                  {!isUnregistered && (
                    <img
                      src={w.image_url || ""}
                      alt={w.full_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  <div className="flex flex-col">
                    <p className="font-semibold text-sm text-[#302B2B]">
                      {isUnregistered ? "Unregistered User" : w.full_name}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-[#9A99AB]">
                      <Mail className="w-3 h-3" />
                      <span>{w.worker_email || "No email"}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm mt-1 pt-2 border-t border-[#EDEEF3]">
                  <div className="flex flex-col items-center w-1/3">
                    <div className="flex items-center gap-1 text-[#302B2B]">
                      <span className="font-bold">{w.revenue ? `${w.revenue}` : "$0"}</span>
                    </div>
                    <span className="text-[#9A99AB] text-xs">Revenue</span>
                  </div>

                  <div className="flex flex-col items-center w-1/3">
                    <div className="flex items-center gap-1 text-[#302B2B]">
                      <Briefcase className="w-4 h-4 text-[#2138CF]" />
                      <span className="font-bold">{w.services_count ?? 0}</span>
                    </div>
                    <span className="text-[#9A99AB] text-xs">Services</span>
                  </div>

                  <div className="flex flex-col items-center w-1/3">
                    <div className="flex items-center gap-1 text-[#302B2B]">
                      <TrendingUp className="w-4 h-4 text-[#F59F0A]" />
                      <span className="font-bold">{w.avg_ticket ? `${w.avg_ticket}` : "$0"}</span>
                    </div>
                    <span className="text-[#9A99AB] text-xs">Avg. Ticket</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}